/**
 * What label a failed API request is filed under, and how to spot the one case
 * where our own deadline is not evidence of anything.
 *
 * Two consumers have to agree on this: `deliverApiFailure`'s per-session dedupe
 * signature in `api.js`, and the `api_status` tag plus fingerprint in
 * `sentry.js`. When those disagree the client and Sentry stop meaning the same
 * thing by "the same failure", which is the mis-grouping trap `lessons.md`
 * records for `api_method`/`api_endpoint`. So the derivation lives here, once.
 *
 * Kept dependency-free and side-effect-free at import time so the policy is
 * unit-testable without a network or a Vue app — same shape as
 * `linkSaveOutcome.js`, `recipeSaveOutcome.js` and `requestRetry.js`.
 */

/**
 * How far past its own deadline an abort has to land before we stop believing
 * the deadline.
 *
 * `api.js` arms `setTimeout(() => controller.abort(), timeout)` and names the
 * resulting error after it — "the server may still be processing", i.e. the
 * backend burned the whole budget. On iOS that premise can be false: every
 * browser there is WKWebView, and backgrounding the app or locking the screen
 * freezes JS timers and stalls the in-flight `fetch`. The already-due abort then
 * fires on resume, minutes later, having measured nothing at all.
 *
 * 1.5x is wide enough to absorb a busy main thread delaying a timer by a few
 * hundred ms, and narrow enough that a genuine hang never qualifies.
 * LINKFLARE-15 filed 681 s and 1399 s against a 60 s deadline, with no server
 * span for either request.
 */
export const SUSPENDED_PAGE_ELAPSED_FACTOR = 1.5

/**
 * Did this abort land so late that the timer must have been frozen?
 *
 * @param {number} elapsedMs — wall clock measured from where the timer was armed
 * @param {number} timeoutMs — the deadline that timer was given
 * @returns {boolean}
 */
export function isSuspendedPageAbort(elapsedMs, timeoutMs) {
  if (!(timeoutMs > 0)) return false
  if (!Number.isFinite(elapsedMs)) return false
  return elapsedMs > timeoutMs * SUSPENDED_PAGE_ELAPSED_FACTOR
}

/**
 * The label for a failure: the HTTP status when one came back, `network` when
 * nothing did, `suspended` for an abort whose timer the OS had frozen, and
 * `landed` for a lost save the server turned out to have (api.js
 * `deferredLostReport`).
 *
 * `suspended` is deliberately its own label rather than a flavour of `network`.
 * Both lack a status, but they are different defects with different owners — a
 * lost request is the connection, a suspended abort is the user's phone — and
 * one must never be able to close the other's issue. `landed` is its own label
 * for the same reason: a save that reached the server must not share a group
 * with one that was really lost.
 *
 * @param {Error} error — the error as reported
 * @param {number|null} status — the HTTP status, or null if no response came back
 * @returns {string}
 */
export function apiStatusLabel(error, status) {
  if (error?.suspendedPage === true) return 'suspended'
  if (error?.landedOnServer === true) return 'landed'
  return status == null ? 'network' : String(status)
}
